'use client'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import type { StackProps } from '@chakra-ui/react'
import { StackDivider, VStack } from '@chakra-ui/react'
import { GrandHeading, GrandNextLink } from 'ui'
import { flatCartItems, getVatRateByCountryCode, priceProductForBuyer } from 'shared-utils'
import {
  type CartItem,
  type ICartItemFlat,
  type ServiceDTO,
  type StorefrontOrderLine,
  type StorefrontOrderLineService
} from 'ecosystem'
import { getCountryCodeFromPaymentConfig } from 'api/utils/payment/getCountryCodeFromPaymentConfig'
import { parseCartSubItemsToBackendOrderSubItems } from 'api/utils/cartSubItems'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useImmer } from 'use-immer'
import { useBuyerVat } from '../../../common/hooks'
import { useCheckoutContext } from '../../hooks'
import { CheckoutCartItem } from './CheckoutCartItem'

type ICheckoutCartProps = StackProps

export interface CheckoutCartOverrides {
  continueShopping?: string
  checkoutCartCartItemsMsg?: string
}

export const CheckoutCart: FC<ICheckoutCartProps> = ({ ...props }) => {
  const {
    setOrderLines,
    onRemoveFromCart,
    onAddToCart,
    cartState: { cartItems, cartDiscountCode },
    services,
    paymentProviderConfig
  } = useCheckoutContext()

  const overrides = useOverridesContext<keyof CheckoutCartOverrides>()

  // Rate + price for the resolved buyer. This is the one place the two move
  // together: every payment provider, the invoice handlers, the review panel
  // and the confirmation page all read the lines built below.
  const buyerVat = useBuyerVat(paymentProviderConfig)

  const [selectedServicesPerItem, setSelectedServicesPerItem] = useImmer<Record<string, string[]>>(
    {}
  )

  const storefrontOrderItems: StorefrontOrderLine[] = useMemo(() => {
    const countryCode = getCountryCodeFromPaymentConfig(paymentProviderConfig)
    const vatRate = getVatRateByCountryCode(countryCode)

    return flatCartItems(cartItems).map(
      ({ cartItemId, subItems, qty = 1, product }: ICartItemFlat) => {
        const selectedServicesIds = selectedServicesPerItem[`${cartItemId}${product.id}`]

        const getServiceTotalDiscount = (service: ServiceDTO, q: number) => {
          return q > 1 ? (q - 1) * service.discountPrice : 0
        }

        const pServices: StorefrontOrderLineService[] =
          services
            ?.filter((s) => selectedServicesIds?.includes(s.id))
            .map((service) => ({
              service,
              quantity: qty,
              totalAmount: service.price + getServiceTotalDiscount(service, qty)
            })) ?? []

        return {
          product,
          quantity: qty,
          ...priceProductForBuyer(product, vatRate, buyerVat),
          subItems: parseCartSubItemsToBackendOrderSubItems(
            subItems,
            cartDiscountCode,
            vatRate,
            buyerVat
          ),
          services: pServices
        }
      }
    )
  }, [
    buyerVat,
    cartDiscountCode,
    cartItems,
    paymentProviderConfig,
    selectedServicesPerItem,
    services
  ])

  // const storefrontOrderItemsOld: StorefrontOrderLine[] = useMemo(() => {
  //   const countryCode = getCountryCodeFromPaymentConfig(paymentProviderConfig)
  //   const vatRate = getVatRateByCountryCode(countryCode)
  //
  //   return productServices
  //     .map((productService) => {
  //       const { serviceIds, productId } = productService
  //
  //       // todo: now only for single
  //
  //
  //
  //       const cartItem = cartItems.find((ci) => ci.type === 'SINGLE' && ci.meta.singleType?.product.id === productId)
  //
  //       if (cartItem?.type === 'GROUP') {
  //         return;
  //       }
  //
  //       if (!cartItem?.meta.singleType?.product) {
  //         return null
  //       }
  //
  //       const { product, subItems } = cartItem.meta.singleType;
  //       const pQty = parseInt(cartItem.qty || '1')
  //       const pServices = (services as ServiceDTO[] | undefined)?.filter((s) =>
  //         product.serviceIds?.includes(s.id)
  //       )
  //
  //       const getServiceTotalAmount = (service: ServiceDTO) => {
  //         return serviceIds.includes(service.id)
  //           ? service.price + getServiceTotalDiscount(service, pQty)
  //           : 0
  //       }
  //       const getServiceQuantity = (service: ServiceDTO) => {
  //         return serviceIds.includes(service.id) ? pQty : 0
  //       }
  //       const getServiceTotalDiscount = (service: ServiceDTO, q: number) => {
  //         return q > 1 ? (q - 1) * service.discountPrice : 0
  //       }
  //
  //       return {
  //         product,
  //         quantity: pQty,
  //         originalPrice: product.originalPrice,
  //         unitPrice: product.price,
  //         vatRate,
  //         subItems: parseCartSubItemsToBackendOrderSubItems(
  //           subItems,
  //           cartDiscountCode,
  //           vatRate
  //         ),
  //         services:
  //           pServices
  //             ?.map((service) => ({
  //               service,
  //               quantity: getServiceQuantity(service),
  //               totalAmount: getServiceTotalAmount(service)
  //             }))
  //             .filter((s) => s.quantity) || []
  //       }
  //     })
  //     .filter(notEmpty)
  // }, [cartDiscountCode, cartItems, paymentProviderConfig, productServices, services])

  // const handleSelectedProductServices = (productId: string, serviceIds: string[]) => {
  //   setProductServices((prevState) => {
  //     const current = [...prevState]
  //     const existingProduct = current.find((i) => i.productId === productId)
  //
  //     if (existingProduct) {
  //       existingProduct.serviceIds = serviceIds
  //       return current
  //     }
  //     return [...current, { productId, serviceIds }]
  //   })
  // }

  const handleSelectedProductServicesNew = ({
    cartItem,
    productId,
    serviceIds
  }: {
    cartItem: CartItem
    productId: string
    serviceIds: string[]
  }) => {
    setSelectedServicesPerItem((prevState) => {
      prevState[`${cartItem.id}${productId}`] = serviceIds
    })
  }

  useEffect(() => {
    setOrderLines(storefrontOrderItems)
  }, [setOrderLines, storefrontOrderItems])

  const handleUpdateCartItemAndSubitems = useCallback(
    (cartItem: CartItem) => {
      const updatedItem = structuredClone(cartItem)

      if (updatedItem.type === 'SINGLE') {
        updatedItem.meta.singleType.subItems =
          updatedItem.meta.singleType.subItems?.map((s) => ({
            ...s,
            qty: s.originalQty ? s.originalQty * cartItem.qty : cartItem.qty
          })) || []
      } else if (updatedItem.type === 'GROUP') {
        updatedItem.meta.groupType.items.forEach((detail) => {
          const detailQty = detail.originalQty ? detail.originalQty * cartItem.qty : cartItem.qty
          detail.qty = detailQty

          detail.subItems?.forEach((subItem) => {
            subItem.qty = subItem.originalQty ? subItem.originalQty * detailQty : detail.qty
          })
        })
      }
      onAddToCart(updatedItem)
    },
    [onAddToCart]
  )

  return (
    <VStack
      className="CheckoutCart"
      alignItems="flex-start"
      divider={<StackDivider />}
      bg="background.default"
      borderRadius="sm"
      {...props}
    >
      <GrandNextLink href="/" isButton mb={5}>
        {overrideText('Fortsätt shoppa', overrides?.continueShopping)}
      </GrandNextLink>

      <GrandHeading
        fontSize="lg"
        headingTag="h3"
        title={`${cartItems.length} ${overrideText(
          'produkter i varukorgen',
          overrides?.checkoutCartCartItemsMsg
        )}`}
      />
      <VStack divider={<StackDivider />} spacing={4} w="full">
        {cartItems.map((cartItem: CartItem) => {
          return (
            <CheckoutCartItem
              key={cartItem.id}
              cartItem={cartItem}
              onDelete={(item) => {
                onRemoveFromCart(item)
              }}
              onUpdate={handleUpdateCartItemAndSubitems}
              onServicesIdsUpdate={(productId, serviceIds) => {
                handleSelectedProductServicesNew({ cartItem, productId, serviceIds })
              }}
              size="lg"
            />
          )
        })}
      </VStack>
    </VStack>
  )
}
